import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';

export const AUTH_TOKEN_STORAGE_KEY = 'noisy_user_token';
export const AUTH_EMAIL_STORAGE_KEY = 'noisy_user_email';
export const AUTH_USER_ID_STORAGE_KEY = 'noisy_user_id';
export const AUTH_ADMIN_STORAGE_KEY = 'noisy_user_admin';

type AuthSessionState = {
  token: string | null;
  email: string | null;
  userId: number | null;
  isAdmin: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  private readonly stateSubject = new BehaviorSubject<AuthSessionState>(this.loadState());
  readonly state$ = this.stateSubject.asObservable();
  readonly authenticated$ = this.state$.pipe(map((state) => Boolean(state.token)));

  getState(): AuthSessionState {
    return this.stateSubject.value;
  }

  setSession(token: string, email: string, userId: number, isAdmin = false): void {
    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    localStorage.setItem(AUTH_EMAIL_STORAGE_KEY, email);
    localStorage.setItem(AUTH_USER_ID_STORAGE_KEY, String(userId));
    localStorage.setItem(AUTH_ADMIN_STORAGE_KEY, String(isAdmin));
    this.stateSubject.next({ token, email, userId, isAdmin });
  }

  clearSession(): void {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_EMAIL_STORAGE_KEY);
    localStorage.removeItem(AUTH_USER_ID_STORAGE_KEY);
    localStorage.removeItem(AUTH_ADMIN_STORAGE_KEY);
    this.stateSubject.next({ token: null, email: null, userId: null, isAdmin: false });
  }

  private loadState(): AuthSessionState {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    const email = localStorage.getItem(AUTH_EMAIL_STORAGE_KEY);
    const rawUserId = localStorage.getItem(AUTH_USER_ID_STORAGE_KEY);
    const userId = rawUserId ? Number(rawUserId) : null;
    const isAdmin = localStorage.getItem(AUTH_ADMIN_STORAGE_KEY) === 'true';

    return {
      token,
      email,
      userId: Number.isFinite(userId) ? userId : null,
      isAdmin,
    };
  }
}
